import { useMeFavouritePartner, useSettings } from '@capsicum/common';
import { useAvailableMarketsPerPartnerQuery } from '@capsicum/socios-core';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type NCWalletTokenCardsBalanceEntry, type NCWalletTokenCardsEntry } from '../../models';
import { useEmptyNCWalletTokenBalanceCreator } from '../useEmptyNCWalletTokenBalanceCreator';
import { useTopMovingTokensAnalytics } from '../useTopMovingTokensAnalytics';

export const TOP_MOVING_TOKENS_MIN_ENTRIES = 3;
export const TOP_MOVING_TOKENS_MAX_ENTRIES = 8;

type UseNCWalletTopMovingTokensProps = {
	excludeShortCodes: Array<string>;
};

export const useNCWalletTopMovingTokens = ({ excludeShortCodes }: UseNCWalletTopMovingTokensProps) => {
	const [viewedCurrencyIDs, setViewedCurrencyIDs] = useState<Array<string>>([]);
	const { isLoading: isAnalyticsLoading, sendAnalytics } = useTopMovingTokensAnalytics();
	const amount = Math.max(TOP_MOVING_TOKENS_MIN_ENTRIES, TOP_MOVING_TOKENS_MAX_ENTRIES - excludeShortCodes.length);

	const {
		areSettingsLoading,
		settings: { isWalletFavouriteTeamFTSectionEnabled }
	} = useSettings();

	const { favoritePartner, isFavouritePartnerLoading } = useMeFavouritePartner({
		enabled: isWalletFavouriteTeamFTSectionEnabled
	});

	const { create: createEmptyNCWalletTokenBalance, isLoading: isEmptyNCWalletTokenBalanceCreatorLoading } =
		useEmptyNCWalletTokenBalanceCreator();

	const { data, isLoading: isAvailableMarketsLoading } = useAvailableMarketsPerPartnerQuery(({ data }) => data, {
		query: {
			direction: 'desc',
			// Ideally we send a query parameter to filter out FTs that the user already owns, however this is not currently
			// supported. For this reason, we are requesting more since we need to avoid:
			// FTs that are owned by the user - i.e. nonFavouriteTeamTokens.length.
			// FTs that belong to the user's Favourite Team - i.e. +1.
			limit: excludeShortCodes.length + 1 + amount,
			onlyWithAvailableMarket: true,
			orderBy: 'evolutionPercentage'
		}
	});

	const favouritePartnerCurrencyID = favoritePartner?.currencyId;

	const balances = useMemo(() => {
		if (!data || isEmptyNCWalletTokenBalanceCreatorLoading) return [];

		const balances: Array<NCWalletTokenCardsBalanceEntry> = [];

		for (let partnerIndex = 0; partnerIndex < data.length; partnerIndex++) {
			const partner = data[partnerIndex];

			const baseCurrency = partner?.markets?.[0]?.baseCurrency;

			if (!baseCurrency) continue;

			const { id: currencyId, shortCode } = baseCurrency;

			if (
				!shortCode ||
				!currencyId ||
				excludeShortCodes.includes(shortCode) ||
				(isWalletFavouriteTeamFTSectionEnabled && currencyId === favouritePartnerCurrencyID)
			)
				continue;

			const balance = createEmptyNCWalletTokenBalance({ isTopMovingToken: true, partner });

			if (balance) balances.push(balance);

			if (balances.length >= amount) break;
		}

		return balances;
	}, [
		data,
		amount,
		excludeShortCodes,
		favouritePartnerCurrencyID,
		createEmptyNCWalletTokenBalance,
		isWalletFavouriteTeamFTSectionEnabled,
		isEmptyNCWalletTokenBalanceCreatorLoading
	]);

	useEffect(() => {
		// Debounce logic to stop from triggering the analytics twice on dev mode, due to strict mode.
		const timeoutID = setTimeout(() => {
			const newCurrencyIDs = balances.reduce<Array<string>>((newCurrencyIDs, { currencyId }) => {
				if (!viewedCurrencyIDs.includes(currencyId)) newCurrencyIDs.push(currencyId);

				return newCurrencyIDs;
			}, []);

			if (!newCurrencyIDs.length) return;

			newCurrencyIDs.forEach(currencyID =>
				sendAnalytics({
					currencyID,
					type: 'view'
				})
			);

			setViewedCurrencyIDs(viewedCurrencyIDs => [...viewedCurrencyIDs, ...newCurrencyIDs]);
		}, 500);

		return () => clearTimeout(timeoutID);
	}, [balances, sendAnalytics, viewedCurrencyIDs]);

	const onClick = useCallback(
		(entry: NCWalletTokenCardsEntry) => {
			if (entry.type !== 'balance') return;

			sendAnalytics({
				currencyID: entry.currencyId,
				type: 'click'
			});
		},
		[sendAnalytics]
	);

	return {
		balances,
		isLoading:
			isAnalyticsLoading ||
			areSettingsLoading ||
			isFavouritePartnerLoading ||
			isAvailableMarketsLoading ||
			isEmptyNCWalletTokenBalanceCreatorLoading,
		onClick
	};
};
